import { useEffect, useState } from 'react';
import { NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { BrandLockup, Button, Icon, Spinner } from '@onepos/ui';
import type { IconName } from '@onepos/ui';
import { AuthProvider, useAuth } from './lib/auth';
import type { PlatformMe } from './lib/types';
import { initials } from './lib/format';
import { LoginPage, NotAdminPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { RestaurantsPage } from './pages/RestaurantsPage';
import { RestaurantPage } from './pages/RestaurantPage';
import { NewRestaurantPage } from './pages/NewRestaurantPage';
import { AuditPage } from './pages/AuditPage';
import { PlansPage } from './pages/PlansPage';
import { LibraryPage } from './pages/LibraryPage';
import { UiStatsPage } from './pages/UiStatsPage';
import { DesignSystemPage } from './pages/DesignSystemPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { BrandPage } from './pages/BrandPage';
import { SupportBanner } from './components/SupportBanner';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Bosh sahifa', icon: 'home' },
  { to: '/restoranlar', label: 'Restoranlar', icon: 'store' },
  { to: '/tariflar', label: 'Tariflar', icon: 'card' },
  { to: '/kutubxona', label: 'Menyu kutubxonasi', icon: 'grid' },
  { to: '/korinish', label: 'Ko‘rinish statistikasi', icon: 'sparkle' },
  { to: '/brend', label: 'Brend', icon: 'layers' },
  { to: '/jurnal', label: 'Jurnal', icon: 'list' },
];

const ROLE_LABEL: Record<PlatformMe['role'], string> = {
  owner: 'Platforma egasi',
  support: 'Yordam xodimi',
  billing: 'Hisob-kitob',
};

export function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

function Gate() {
  const { state } = useAuth();
  if (state.status === 'loading') {
    return (
      <div className="cn-center">
        <Spinner />
      </div>
    );
  }
  if (state.status === 'signed_out') return <LoginPage message={state.message} />;
  if (state.status === 'not_admin') return <NotAdminPage email={state.email} />;
  return <Shell me={state.me} />;
}

function Shell({ me }: { me: PlatformMe }) {
  const { signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="cn-shell">
      <aside className={`cn-side${menuOpen ? ' cn-side--open' : ''}`}>
        <div className="cn-side__top">
          <NavLink to="/" className="cn-brand cn-brand--one" aria-label="ONE POS bosh sahifa">
            <BrandLockup height={30} tone="side" title="ONE POS" />
            <span className="cn-brand__sub">Platforma paneli</span>
          </NavLink>
          <button
            type="button"
            className="cn-menu-btn"
            aria-label={menuOpen ? 'Menyuni yopish' : 'Menyuni ochish'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <Icon name={menuOpen ? 'x' : 'menu'} />
          </button>
        </div>
        <div className="cn-side__body">
          {me.role !== 'billing' && (
            <NavLink to="/restoranlar/yangi" className="op-btn op-btn--mint">
              <Icon name="plus" /> Yangi restoran
            </NavLink>
          )}
          <nav className="cn-nav" aria-label="Asosiy menyu">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'}>
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="cn-user">
            <span className="cn-user__avatar" aria-hidden="true">
              {initials(me.full_name)}
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span className="cn-user__name">{me.full_name}</span>
              <span className="cn-user__role">{ROLE_LABEL[me.role]}</span>
            </span>
            <Button variant="ghost" iconOnly aria-label="Chiqish" title="Chiqish" className="cn-user__out" onClick={() => void signOut()}>
              <Icon name="logout" />
            </Button>
          </div>
        </div>
      </aside>
      <main className="cn-main">
        {me.support && <SupportBanner support={me.support} />}
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/restoranlar" element={<RestaurantsPage />} />
          <Route path="/restoranlar/yangi" element={<NewRestaurantPage />} />
          <Route path="/restoranlar/:id" element={<RestaurantPage />} />
          <Route path="/tariflar" element={<PlansPage />} />
          <Route path="/kutubxona" element={<LibraryPage />} />
          <Route path="/korinish" element={<UiStatsPage />} />
          <Route path="/brend" element={<BrandPage />} />
          <Route path="/jurnal" element={<AuditPage />} />
          <Route path="/dizayn" element={<DesignSystemPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
    </div>
  );
}
